import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import type { ColumnDef, OnChangeFn, PaginationState, SortingState } from "@tanstack/react-table";
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  FileText,
  List,
  Loader2,
  MoreVertical,
  Plus,
  Sparkles,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { DataTable } from "@/components/admin/DataTable";
import { SearchInput } from "@/components/admin/SearchInput";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { ATargetList } from "@/components/topics/ATargetList";
import { TopicReviewQueue } from "@/components/topics/TopicReviewQueue";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  topicKeys,
  useApproveTopic,
  useArchetypes,
  useCreateTopic,
  useDeleteTopic,
  useDraftTopics,
  useEvidenceRichness,
  useGenerationJobs,
  useRejectTopic,
  useTopics,
  useTopicTags,
  useUpdateTopic,
} from "@/hooks/admin/topics";
import { AdminLayout } from "@/layouts/AdminLayout";
import { TOPIC_MULTI_DIMS, TOPIC_SINGLE_DIMS } from "@/lib/topicDimensions";
import { cn } from "@/lib/utils";
import type { GenerationJob, TagResponse, TaskStatus, Topic } from "@/types";

type StatusFilter = "all" | "ready" | "assigned" | "drafts";
type ActiveView = "list" | "queue" | "generate";

const PAGE_SIZE = 20;
const JOBS_PAGE_SIZE = 20;

// The topics list endpoint only sorts on these columns.
const SORTABLE = new Set(["title", "created_at"]);

const topicFormSchema = z.object({
  title: z.string().trim().min(1, "Title is required"),
  description: z.string().trim().min(1, "Description is required"),
  context: z.string(),
  core_question: z.string(),
  guidance: z.string(),
  evidence_hints: z.string(),
  // Selected tag ids per multi-value dimension (topic_area, angle_type, sector, …).
  tag_ids_by_dim: z
    .record(z.string(), z.array(z.string()))
    .refine((v) => (v.topic_area ?? []).length > 0, {
      message: "At least one topic area is required.",
    }),
  time_mode_id: z.string().nullable(),
  difficulty_id: z.string().nullable(),
  archetype_id: z.string().nullable(),
  // "approved" | "needs_review" — collapsed Draft/Ready toggle.
  task_status: z.enum(["approved", "needs_review"]),
});
type TopicFormValues = z.infer<typeof topicFormSchema>;

function toFormValues(topic?: Topic): TopicFormValues {
  const topicTags = topic ? (topic.topic_tags ?? topic.tags) : [];
  const tagIdsByDim: Record<string, string[]> = {};
  for (const dim of TOPIC_MULTI_DIMS) {
    tagIdsByDim[dim.key] = topicTags.filter((t) => t.tag_group === dim.key).map((t) => t.id);
  }
  return {
    title: topic?.title ?? "",
    description: topic?.description ?? "",
    context: topic?.context ?? "",
    core_question: topic?.core_question ?? "",
    guidance: topic?.guidance ?? "",
    evidence_hints: topic?.evidence_hints ?? "",
    tag_ids_by_dim: tagIdsByDim,
    time_mode_id: topic?.time_mode_id ?? null,
    difficulty_id: topic?.difficulty_id ?? null,
    archetype_id: topic?.archetype_id ?? null,
    task_status:
      topic?.task_status === "approved" || topic?.task_status === "edited_approved"
        ? "approved"
        : "needs_review",
  };
}

const TEXT_FIELDS = [
  { name: "context", label: "Context", rows: 2, placeholder: "Market or macro context…" },
  {
    name: "core_question",
    label: "Core Question",
    rows: 2,
    placeholder: "The central analytical question…",
  },
  {
    name: "guidance",
    label: "Guidance",
    rows: 3,
    placeholder: "Approach guidance and analytical framework hints…",
  },
  {
    name: "evidence_hints",
    label: "Evidence Hints",
    rows: 2,
    placeholder: "Data sources, charts, or evidence the candidate should reference…",
  },
] as const;

const SELECT_CLASS =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500";

interface TopicFormModalProps {
  open: boolean;
  topic?: Topic;
  allTagsByGroup: Record<string, TagResponse[]>;
  onClose: () => void;
  onSaved: () => void;
}

function TopicFormModal({ open, topic, allTagsByGroup, onClose, onSaved }: TopicFormModalProps) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [classificationOpen, setClassificationOpen] = useState(true);
  const createTopic = useCreateTopic();
  const updateTopic = useUpdateTopic();
  const archetypesQuery = useArchetypes();
  const archetypes = archetypesQuery.data ?? [];
  const form = useForm<TopicFormValues>({
    resolver: zodResolver(topicFormSchema),
    defaultValues: toFormValues(topic),
  });
  const { control, reset, watch } = form;
  const isSubmitting = form.formState.isSubmitting;

  useEffect(() => {
    if (open) {
      reset(toFormValues(topic));
      setSubmitError(null);
    }
  }, [open, topic, reset]);

  const angleTypeIds = watch("tag_ids_by_dim.angle_type") ?? [];
  const difficultyId = watch("difficulty_id");
  const archetypeId = watch("archetype_id");
  // Evidence richness is auto-computed server-side from angle type + difficulty.
  const evidenceRichnessQuery = useEvidenceRichness(angleTypeIds, difficultyId);
  const evidenceRichness =
    angleTypeIds.length > 0
      ? (evidenceRichnessQuery.data?.value ?? topic?.evidence_richness?.display_name ?? null)
      : null;
  const selectedArchetype = archetypes.find((a) => a.id === archetypeId) ?? null;

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    const { tag_ids_by_dim, ...rest } = values;
    const payload = {
      ...rest,
      title: values.title.trim(),
      description: values.description.trim(),
      context: values.context.trim() || null,
      core_question: values.core_question.trim() || null,
      guidance: values.guidance.trim() || null,
      evidence_hints: values.evidence_hints.trim() || null,
      topic_area_ids: tag_ids_by_dim.topic_area ?? [],
      tag_ids: Object.entries(tag_ids_by_dim).flatMap(([key, ids]) =>
        key === "topic_area" ? [] : ids
      ),
    };
    try {
      if (topic) {
        const { task_status, ...data } = payload;
        await updateTopic.mutateAsync({
          id: topic.id,
          data: { ...data, ...(task_status !== topic.task_status && { task_status }) },
        });
      } else {
        await createTopic.mutateAsync(payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to save topic");
    }
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 text-xl">
            {topic ? "Edit Topic" : "Create New Topic"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 transition-colors hover:bg-gray-100"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <Form {...form}>
          <form onSubmit={onSubmit} className="max-h-[calc(90vh-80px)] overflow-y-auto p-6">
            {submitError && (
              <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{submitError}</span>
              </div>
            )}

            <div className="space-y-4">
              <FormField
                control={control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Title <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={String(field.value ?? "")}
                        placeholder="e.g., Economic Impact Analysis"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Description <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        value={String(field.value ?? "")}
                        rows={3}
                        placeholder="Describe the topic and what the assignment should cover..."
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {TEXT_FIELDS.map((f) => (
                <FormField
                  key={f.name}
                  control={control}
                  name={f.name}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{f.label}</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          value={String(field.value ?? "")}
                          rows={f.rows}
                          placeholder={f.placeholder}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}

              <FormField
                control={control}
                name="archetype_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Archetype</FormLabel>
                    <select
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value || null)}
                      className={SELECT_CLASS}
                    >
                      <option value="">— none —</option>
                      {archetypes.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                          {a.difficulty ? ` (${a.difficulty})` : ""}
                        </option>
                      ))}
                    </select>
                    {selectedArchetype?.question_template && (
                      <div className="mt-2 rounded-lg border border-purple-100 bg-purple-50 px-3 py-2 text-purple-800 text-sm">
                        <span className="font-medium text-purple-600 text-xs uppercase tracking-wide">
                          Question template:
                        </span>{" "}
                        {selectedArchetype.question_template}
                      </div>
                    )}
                  </FormItem>
                )}
              />

              {selectedArchetype && (
                <ATargetList
                  targets={selectedArchetype.a_targets.map((t) => ({
                    code: t.component_code,
                    behaviour: t.assessment_behaviour,
                    exampleWording: t.example_wording,
                    strength: t.strength,
                  }))}
                />
              )}

              <div className="overflow-hidden rounded-xl border border-gray-200">
                <button
                  type="button"
                  onClick={() => setClassificationOpen((o) => !o)}
                  className="flex w-full items-center justify-between bg-gray-50 px-5 py-3 font-semibold text-gray-700 text-sm transition-colors hover:bg-gray-100"
                >
                  Classification
                  {classificationOpen ? (
                    <ChevronUp className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  )}
                </button>
                {classificationOpen && (
                  <div className="space-y-4 p-5">
                    <FormField
                      control={control}
                      name="tag_ids_by_dim"
                      render={({ field }) => (
                        <FormItem>
                          {TOPIC_MULTI_DIMS.map((dim) => {
                            const tags = allTagsByGroup[dim.key] ?? [];
                            if (tags.length === 0) return null;
                            const selected = field.value[dim.key] ?? [];
                            return (
                              <div key={dim.key}>
                                <p className="mb-2 font-medium text-gray-600 text-xs uppercase tracking-wide">
                                  {dim.label}
                                  {dim.required && <span className="ml-1 text-red-500">*</span>}
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                  {tags.map((tag) => {
                                    const isSelected = selected.includes(tag.id);
                                    return (
                                      <button
                                        key={tag.id}
                                        type="button"
                                        onClick={() =>
                                          field.onChange({
                                            ...field.value,
                                            [dim.key]: isSelected
                                              ? selected.filter((x) => x !== tag.id)
                                              : [...selected, tag.id],
                                          })
                                        }
                                        className={cn(
                                          "rounded-full border px-2.5 py-1 font-medium text-xs transition-colors",
                                          isSelected
                                            ? dim.key === "topic_area"
                                              ? "border-green-400 bg-green-100 text-green-800"
                                              : dim.key === "angle_type"
                                                ? "border-purple-400 bg-purple-100 text-purple-800"
                                                : "border-indigo-400 bg-indigo-100 text-indigo-800"
                                            : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                                        )}
                                      >
                                        {tag.display_name}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {TOPIC_SINGLE_DIMS.map((dim) => {
                      const tags = allTagsByGroup[dim.key] ?? [];
                      if (tags.length === 0) return null;
                      return (
                        <FormField
                          key={dim.key}
                          control={control}
                          name={`${dim.key}_id`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-gray-600 text-xs uppercase tracking-wide">
                                {dim.label}
                              </FormLabel>
                              <select
                                value={field.value ?? ""}
                                onChange={(e) => field.onChange(e.target.value || null)}
                                className={SELECT_CLASS}
                              >
                                <option value="">— not set —</option>
                                {tags.map((tag) => (
                                  <option key={tag.id} value={tag.id}>
                                    {tag.display_name}
                                  </option>
                                ))}
                              </select>
                            </FormItem>
                          )}
                        />
                      );
                    })}

                    {/* Evidence Richness — read-only, auto-computed */}
                    <div>
                      <p className="mb-2 font-medium text-gray-600 text-xs uppercase tracking-wide">
                        Evidence Richness
                      </p>
                      {evidenceRichness ? (
                        <div className="flex items-center gap-2">
                          <span
                            className={cn(
                              "inline-flex rounded-full px-2.5 py-1 font-medium text-xs",
                              evidenceRichness === "Low"
                                ? "bg-amber-100 text-amber-700"
                                : evidenceRichness === "High"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-blue-100 text-blue-700"
                            )}
                          >
                            {evidenceRichness === "Low" && "⚠ "}
                            {evidenceRichness}
                          </span>
                          <span className="text-gray-400 text-xs">
                            auto-computed from angle type + difficulty
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs italic">
                          Set angle type to compute
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <FormField
                control={control}
                name="task_status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => field.onChange("needs_review")}
                        className={cn(
                          "flex-1 rounded-lg border px-4 py-2.5 font-medium transition-colors",
                          field.value !== "approved"
                            ? "border-gray-400 bg-gray-100 text-gray-900"
                            : "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                        )}
                      >
                        Draft
                      </button>
                      <button
                        type="button"
                        onClick={() => field.onChange("approved")}
                        className={cn(
                          "flex-1 rounded-lg border px-4 py-2.5 font-medium transition-colors",
                          field.value === "approved"
                            ? "border-green-400 bg-green-100 text-green-800"
                            : "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                        )}
                      >
                        Ready
                      </button>
                    </div>
                    <p className="mt-1 text-gray-500 text-sm">
                      Draft topics won't be assigned to users
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-6">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-4 py-2 text-gray-700 transition-colors hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-white transition-colors hover:bg-primary/90 disabled:bg-primary/40"
              >
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {topic ? "Save Changes" : "Create Topic"}
              </button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}

function DeleteConfirmModal({
  open,
  topic,
  onClose,
  onConfirm,
}: {
  open: boolean;
  topic: Topic | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const deleteTopic = useDeleteTopic();
  const isDeleting = deleteTopic.isPending;

  const handleDelete = async () => {
    if (!topic) return;
    try {
      await deleteTopic.mutateAsync(topic.id);
      onConfirm();
      onClose();
    } catch (err) {
      console.error("Failed to delete topic:", err);
    }
  };

  if (!open || !topic) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <h2 className="mb-2 font-semibold text-gray-900 text-xl">Delete Topic</h2>
        <p className="mb-6 text-gray-600">
          Are you sure you want to delete "{topic.title}"? This action cannot be undone.
        </p>
        {topic.times_used > 0 && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-700">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm">This topic has been used {topic.times_used} time(s).</span>
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-gray-700 transition-colors hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className="flex items-center gap-2 rounded-lg bg-red-500 px-4 py-2 text-white transition-colors hover:bg-red-600 disabled:bg-red-300"
          >
            {isDeleting && <Loader2 className="h-4 w-4 animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function getTopicStatus(topic: Topic): "draft" | "ready" | "assigned" {
  const assignable = ["approved", "edited_approved"] as TaskStatus[];
  if (!assignable.includes(topic.task_status)) return "draft";
  if (topic.times_used > 0) return "assigned";
  return "ready";
}

export default function AdminTopicsPage() {
  const [searchParams] = useSearchParams();

  const initialView = (searchParams.get("view") as ActiveView) || "list";
  const initialJobsStatus = searchParams.get("status") || "completed";
  const [activeView, setActiveView] = useState<ActiveView>(initialView);

  const [searchQuery, setSearchQuery] = useState("");
  // topic_area_id filter — preserved from the original; never wired to a UI control.
  const selectedTopicAreaFilter = "";
  const [topicAreaFilter, setTopicAreaFilter] = useState<string>("");
  const [angleTypeFilter, setAngleTypeFilter] = useState<string>("");
  const [evidenceRichnessFilter, setEvidenceRichnessFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ready");
  const [currentPage, setCurrentPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([]);

  const [queueIndex, setQueueIndex] = useState(0);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTopic, setEditingTopic] = useState<Topic | undefined>();
  const [deletingTopic, setDeletingTopic] = useState<Topic | null>(null);

  // Generate tab state
  const [jobsStatusFilter, setJobsStatusFilter] = useState<string>(initialJobsStatus);
  const [jobsPage, setJobsPage] = useState(1);

  const sort = sorting[0];
  const statusParam =
    statusFilter === "all" ? undefined : statusFilter === "drafts" ? "draft" : statusFilter;

  // Tags grouped by tag_group — drives the topic-area / angle-type filters + form.
  const tagsQuery = useTopicTags();
  const allTagsByGroup = tagsQuery.data ?? {};
  const topicAreas = allTagsByGroup.topic_area ?? [];

  // Draft topics feed the Review Queue. Invalidated by approve/save/delete.
  const draftsQuery = useDraftTopics();
  const draftTopics = draftsQuery.data ?? [];

  // The topics list. The full filter set is folded into the query key, so any
  // filter/search/sort/page change refetches naturally — this is what lets the
  // page drop its manual "reload on filter change" loader effect.
  const topicsQuery = useTopics({
    page: currentPage,
    pageSize: PAGE_SIZE,
    topicAreaId: selectedTopicAreaFilter || undefined,
    status: statusParam as "draft" | "ready" | "assigned" | undefined,
    q: searchQuery || undefined,
    sort_by: sort?.id as "title" | "created_at" | undefined,
    sort_order: sort ? (sort.desc ? "desc" : "asc") : undefined,
    includeDrafts: statusFilter === "all",
    topicArea: topicAreaFilter || undefined,
    angleType: angleTypeFilter || undefined,
    evidenceRichness: evidenceRichnessFilter || undefined,
  });
  const topics = topicsQuery.data?.items ?? [];
  const totalPages = topicsQuery.data?.total_pages ?? 1;
  const totalTopics = topicsQuery.data?.total ?? 0;
  const topicsError = topicsQuery.isError
    ? topicsQuery.error instanceof Error
      ? topicsQuery.error.message
      : "Failed to load topics"
    : null;

  // Generation jobs. The list is keyed by status + page and polls itself while
  // any job is still running; we only enable it on the generate view.
  const jobsQuery = useGenerationJobs(
    {
      status: jobsStatusFilter === "all" ? undefined : jobsStatusFilter,
      page: jobsPage,
      pageSize: JOBS_PAGE_SIZE,
    },
    { enabled: activeView === "generate" }
  );
  const generationJobs = jobsQuery.data?.items ?? [];
  const jobsTotalPages = jobsQuery.data?.total_pages ?? 1;
  const jobsTotal = jobsQuery.data?.total ?? 0;

  // Mutations — stable `mutateAsync`/`refetch` fns drive the callbacks below so
  // none of the memos/handlers need a dependency suppression.
  const updateTopic = useUpdateTopic();
  const approveTopic = useApproveTopic();
  const rejectTopic = useRejectTopic();
  const queryClient = useQueryClient();
  const { mutateAsync: updateTopicAsync } = updateTopic;
  const { mutateAsync: approveTopicAsync } = approveTopic;
  const { mutateAsync: rejectTopicAsync } = rejectTopic;
  const { refetch: refetchDrafts } = draftsQuery;
  const isProcessing = approveTopic.isPending || rejectTopic.isPending || updateTopic.isPending;

  // Whenever the (server-owned) draft list shrinks past the cursor, clamp it.
  useEffect(() => {
    if (queueIndex >= draftTopics.length && draftTopics.length > 0) {
      setQueueIndex(draftTopics.length - 1);
    }
  }, [draftTopics.length, queueIndex]);

  const handleApproveTopic = useCallback(
    async (topic: Topic, editedTitle?: string, editedDescription?: string) => {
      try {
        const hasEdits =
          (editedTitle && editedTitle !== topic.title) ||
          (editedDescription && editedDescription !== topic.description);

        if (hasEdits) {
          await updateTopicAsync({
            id: topic.id,
            data: {
              title: editedTitle || topic.title,
              description: editedDescription || topic.description,
            },
          });
        }

        // approve invalidates topics + drafts; queue refreshes from the server.
        await approveTopicAsync(topic.id);
      } catch (error) {
        console.error("Failed to approve topic:", error);
      }
    },
    [approveTopicAsync, updateTopicAsync]
  );

  const handleSkipTopic = useCallback(() => {
    setQueueIndex((prev) => (prev < draftTopics.length - 1 ? prev + 1 : 0));
  }, [draftTopics.length]);

  const handleSaveTopic = useCallback(
    async (topic: Topic, title: string, description: string) => {
      try {
        await updateTopicAsync({ id: topic.id, data: { title, description } });
      } catch (error) {
        console.error("Failed to save topic:", error);
      }
    },
    [updateTopicAsync]
  );

  const handleRejectTopic = useCallback(
    async (topic: Topic, rejectionReason: string) => {
      try {
        await rejectTopicAsync({ id: topic.id, rejectionReason });
        setQueueIndex((prev) => Math.max(0, Math.min(prev, draftTopics.length - 2)));
      } catch (error) {
        console.error("Failed to reject topic:", error);
      }
    },
    [rejectTopicAsync, draftTopics.length]
  );

  // The queue edits fields inline via the API client; refresh the cached lists.
  const handleTopicUpdated = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: topicKeys.all });
  }, [queryClient]);

  const topicColumns = useMemo<ColumnDef<Topic>[]>(
    () => [
      {
        accessorKey: "title",
        header: "Title",
        cell: ({ row }) => {
          const topic = row.original;
          const isLowEvidence = topic.evidence_richness?.value === "Low";
          return (
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">{topic.title}</span>
              {isLowEvidence && (
                <span
                  title="Low evidence richness — admin review recommended"
                  className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-amber-700 text-xs"
                >
                  ⚠ Low Evidence
                </span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "description",
        header: "Description",
        enableSorting: false,
        cell: ({ row }) => {
          const topic = row.original;
          const truncatedDesc =
            topic.description.length > 60
              ? `${topic.description.slice(0, 60)}...`
              : topic.description;
          return (
            <div className="group/tooltip relative">
              <span className="cursor-help text-gray-600 text-sm">{truncatedDesc}</span>
              {topic.description.length > 60 && (
                <div className="absolute top-full left-0 z-50 mt-1 hidden w-80 rounded-lg bg-gray-900 p-3 text-sm text-white shadow-lg group-hover/tooltip:block">
                  {topic.description}
                </div>
              )}
            </div>
          );
        },
      },
      {
        id: "classification",
        header: "Classification",
        enableSorting: false,
        cell: ({ row }) => {
          const topic = row.original;
          const topicAreaTags = (topic.topic_tags ?? topic.tags).filter(
            (t) => t.tag_group === "topic_area"
          );
          const angleTypeTags = (topic.topic_tags ?? topic.tags).filter(
            (t) => t.tag_group === "angle_type"
          );
          return (
            <div className="flex w-52 flex-col gap-1">
              {topicAreaTags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {topicAreaTags.slice(0, 2).map((tag) => (
                    <span
                      key={tag.id}
                      className="inline-flex rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-green-700 text-xs"
                    >
                      {tag.display_name}
                    </span>
                  ))}
                  {topicAreaTags.length > 2 && (
                    <span className="inline-flex rounded-full border border-green-100 bg-green-50 px-2 py-0.5 text-green-600 text-xs">
                      +{topicAreaTags.length - 2}
                    </span>
                  )}
                </div>
              )}
              {angleTypeTags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {angleTypeTags.slice(0, 2).map((tag) => (
                    <span
                      key={tag.id}
                      className="inline-flex rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-purple-700 text-xs"
                    >
                      {tag.display_name}
                    </span>
                  ))}
                  {angleTypeTags.length > 2 && (
                    <span className="inline-flex rounded-full border border-purple-100 bg-purple-50 px-2 py-0.5 text-purple-500 text-xs">
                      +{angleTypeTags.length - 2}
                    </span>
                  )}
                </div>
              )}
              {topicAreaTags.length === 0 && angleTypeTags.length === 0 && (
                <span className="text-gray-400 text-sm italic">—</span>
              )}
            </div>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        enableSorting: false,
        cell: ({ row }) => {
          const status = getTopicStatus(row.original);
          const tone = status === "draft" ? "warning" : status === "assigned" ? "info" : "success";
          const label = status === "draft" ? "Draft" : status === "assigned" ? "Assigned" : "Ready";
          return <StatusBadge tone={tone}>{label}</StatusBadge>;
        },
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => {
          const topic = row.original;
          return (
            <div className="text-right">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="rounded-lg p-2 transition-colors hover:bg-gray-100">
                    <MoreVertical className="h-4 w-4 text-gray-500" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => {
                      setEditingTopic(topic);
                      setShowCreateModal(true);
                    }}
                  >
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setDeletingTopic(topic)}
                    className="text-red-600 focus:text-red-600"
                  >
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    []
  );

  const jobColumns = useMemo<ColumnDef<GenerationJob>[]>(
    () => [
      {
        accessorKey: "seed_event_title",
        header: "Seed Event",
        enableSorting: false,
        cell: ({ row }) => {
          const job = row.original;
          return (
            <div>
              <p className="font-medium text-gray-900 text-sm">
                {job.seed_event_title ?? "Unknown event"}
              </p>
              {job.error_message && (
                <p className="mt-1 flex items-center gap-1 text-red-600 text-xs">
                  <AlertCircle className="h-3 w-3 shrink-0" />
                  {job.error_message}
                </p>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "created_at",
        header: "Started",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-gray-500 text-sm">
            {new Date(row.original.created_at).toLocaleString()}
          </span>
        ),
      },
      {
        id: "topics",
        header: "Topics",
        enableSorting: false,
        cell: ({ row }) => {
          const job = row.original;
          return (
            <span className="text-gray-600 text-sm">
              {job.status === "completed"
                ? `${job.generated_count} / ${job.requested_count}`
                : `— / ${job.requested_count}`}
            </span>
          );
        },
      },
      {
        id: "job_status",
        header: "Status",
        enableSorting: false,
        cell: ({ row }) => {
          const job = row.original;
          const isRunning = job.status === "pending" || job.status === "in_progress";
          const tone = isRunning ? "info" : job.status === "completed" ? "success" : "danger";
          return (
            <StatusBadge tone={tone} className="items-center gap-1.5">
              {isRunning && <Loader2 className="h-3 w-3 animate-spin" />}
              {isRunning
                ? job.status === "pending"
                  ? "Pending"
                  : "Generating…"
                : job.status === "completed"
                  ? "Completed"
                  : "Failed"}
            </StatusBadge>
          );
        },
      },
      {
        id: "job_actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => {
          const job = row.original;
          if (job.status !== "completed" || job.generated_count <= 0) return null;
          return (
            <div className="text-right">
              <button
                type="button"
                onClick={() => {
                  setActiveView("queue");
                  void refetchDrafts();
                }}
                className="ml-auto flex items-center gap-1 font-medium text-green-600 text-xs hover:text-green-800"
              >
                <CheckCircle className="h-3.5 w-3.5" />
                Review Queue
              </button>
            </div>
          );
        },
      },
    ],
    [refetchDrafts]
  );

  const topicPagination: PaginationState = {
    pageIndex: currentPage - 1,
    pageSize: PAGE_SIZE,
  };
  const onTopicPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const next = typeof updater === "function" ? updater(topicPagination) : updater;
    setCurrentPage(next.pageIndex + 1);
  };
  const onTopicSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      const valid = next.filter((s) => SORTABLE.has(s.id));
      setCurrentPage(1);
      return valid;
    });
  };

  const jobsPagination: PaginationState = {
    pageIndex: jobsPage - 1,
    pageSize: JOBS_PAGE_SIZE,
  };
  const onJobsPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const next = typeof updater === "function" ? updater(jobsPagination) : updater;
    setJobsPage(next.pageIndex + 1);
  };

  if (topicsQuery.isPending && tagsQuery.isPending) {
    return (
      <AdminLayout>
        <div className="flex h-full items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="flex h-full flex-col gap-4">
        {/* Tab Navigation */}
        <div className="flex flex-shrink-0 gap-2">
          <button
            type="button"
            onClick={() => setActiveView("list")}
            className={`flex items-center gap-2 rounded-lg px-4 py-2.5 font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              activeView === "list"
                ? "bg-primary text-white shadow-sm"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <List className="h-5 w-5" />
            Topics List
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveView("queue");
              void refetchDrafts();
            }}
            className={`flex items-center gap-2 rounded-lg px-4 py-2.5 font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              activeView === "queue"
                ? "bg-primary text-white shadow-sm"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <FileText className="h-5 w-5" />
            Review Queue
            {draftTopics.length > 0 && (
              <span
                className={`rounded-full px-2 py-0.5 font-bold text-xs ${
                  activeView === "queue" ? "bg-amber-100 text-amber-700" : "bg-amber-500 text-white"
                }`}
              >
                {draftTopics.length}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveView("generate");
              setJobsPage(1);
            }}
            className={`flex items-center gap-2 rounded-lg px-4 py-2.5 font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              activeView === "generate"
                ? "bg-primary text-white shadow-sm"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <Sparkles className="h-5 w-5" />
            Generation Jobs
          </button>
        </div>

        {/* List View */}
        {activeView === "list" && (
          <DataTable
            className="h-full"
            columns={topicColumns}
            data={topics}
            isLoading={topicsQuery.isPending || topicsQuery.isFetching}
            error={topicsError}
            onRetry={() => topicsQuery.refetch()}
            emptyMessage="No topics found"
            getRowId={(t) => t.id}
            toolbar={
              <div className="flex flex-wrap items-center gap-3">
                <SearchInput
                  value={searchQuery}
                  onChange={(v) => {
                    setSearchQuery(v);
                    setCurrentPage(1);
                  }}
                  placeholder="Search topics…"
                />
                {(allTagsByGroup.topic_area?.length ?? 0) > 0 && (
                  <Select
                    value={topicAreaFilter || "all"}
                    onValueChange={(v) => {
                      setTopicAreaFilter(v === "all" ? "" : v);
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="All Topic Areas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Topic Areas</SelectItem>
                      {allTagsByGroup.topic_area?.map((tag) => (
                        <SelectItem key={tag.id} value={tag.value}>
                          {tag.display_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {(allTagsByGroup.angle_type?.length ?? 0) > 0 && (
                  <Select
                    value={angleTypeFilter || "all"}
                    onValueChange={(v) => {
                      setAngleTypeFilter(v === "all" ? "" : v);
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="All Angle Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Angle Types</SelectItem>
                      {allTagsByGroup.angle_type?.map((tag) => (
                        <SelectItem key={tag.id} value={tag.value}>
                          {tag.display_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Select
                  value={evidenceRichnessFilter || "all"}
                  onValueChange={(v) => {
                    setEvidenceRichnessFilter(v === "all" ? "" : v);
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="All Evidence Richness" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Evidence Richness</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>
                <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
                  {(["all", "ready", "assigned", "drafts"] as StatusFilter[]).map((filter) => (
                    <button
                      key={filter}
                      type="button"
                      onClick={() => {
                        setStatusFilter(filter);
                        setCurrentPage(1);
                      }}
                      className={cn(
                        "rounded-md px-3 py-1.5 font-medium text-sm transition-colors",
                        statusFilter === filter
                          ? "bg-white text-gray-900 shadow-sm"
                          : "text-gray-500 hover:text-gray-900"
                      )}
                    >
                      {filter.charAt(0).toUpperCase() + filter.slice(1)}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setEditingTopic(undefined);
                    setShowCreateModal(true);
                  }}
                  className="ml-auto flex flex-shrink-0 items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
                >
                  <Plus className="h-4 w-4" />
                  Create Topic
                </button>
              </div>
            }
            manualPagination
            pageCount={totalPages}
            rowCount={totalTopics}
            pagination={topicPagination}
            onPaginationChange={onTopicPaginationChange}
            manualSorting
            sorting={sorting}
            onSortingChange={onTopicSortingChange}
          />
        )}

        {/* Queue View */}
        {activeView === "queue" && (
          <div className="scrollbar-thin min-h-0 flex-1 overflow-auto">
            <TopicReviewQueue
              topics={draftTopics}
              currentIndex={queueIndex}
              onApprove={handleApproveTopic}
              onReject={handleRejectTopic}
              onSkip={handleSkipTopic}
              onSave={handleSaveTopic}
              onTopicUpdated={handleTopicUpdated}
              isProcessing={isProcessing}
              topicAreas={topicAreas}
              allTagsByGroup={allTagsByGroup}
            />
          </div>
        )}

        {/* Generation Jobs View */}
        {activeView === "generate" && (
          <DataTable
            className="h-full"
            columns={jobColumns}
            data={generationJobs}
            isLoading={jobsQuery.isPending && generationJobs.length === 0}
            emptyMessage="No jobs found"
            getRowId={(j) => j.id}
            toolbar={
              <div className="flex items-center justify-between gap-4">
                <p className="text-sm text-gray-600">
                  Trigger new jobs from{" "}
                  <Link to="/admin/seed-events" className="text-green-600 hover:text-green-700">
                    Seed Events
                  </Link>
                </p>
                <div className="inline-flex shrink-0 rounded-lg border border-gray-200 bg-gray-50 p-1">
                  {(
                    [
                      { key: "all", label: "All" },
                      { key: "in_progress", label: "In Progress" },
                      { key: "completed", label: "Completed" },
                      { key: "failed", label: "Failed" },
                    ] as const
                  ).map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setJobsStatusFilter(key);
                        setJobsPage(1);
                      }}
                      className={`rounded-md px-3 py-1.5 font-medium text-sm transition-colors ${
                        jobsStatusFilter === key
                          ? "bg-white text-gray-900 shadow-sm"
                          : "text-gray-500 hover:text-gray-900"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            }
            manualPagination
            pageCount={jobsTotalPages}
            rowCount={jobsTotal}
            pagination={jobsPagination}
            onPaginationChange={onJobsPaginationChange}
          />
        )}
      </div>

      {/* Modals */}
      <TopicFormModal
        open={showCreateModal}
        topic={editingTopic}
        allTagsByGroup={allTagsByGroup}
        onClose={() => {
          setShowCreateModal(false);
          setEditingTopic(undefined);
        }}
        onSaved={() => setCurrentPage(1)}
      />

      <DeleteConfirmModal
        open={!!deletingTopic}
        topic={deletingTopic}
        onClose={() => setDeletingTopic(null)}
        onConfirm={() => {}}
      />
    </AdminLayout>
  );
}
